import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import type { DateRange } from 'react-day-picker'
import { useAppAuth } from '@/contexts/app-auth.context'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Text } from '@/components/ui/typography'
import { Label } from '@/components/ui/label'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import {
  directoryApiClient,
  type Staff,
  type RoleWithMembers,
  type ScheduleEntry,
  type SubstituteInput,
} from '@/lib/api/directory.client'
import { directoryAdminApiClient } from '@/lib/api/directory-admin.client'
import {
  TimezoneSelector,
  type TimezoneEnumValue,
} from '@/components/common/TimezoneSelector'
import { toIANATimezone, toTimezoneEnumValue } from '@super/shared-types'
import { DateTime } from 'luxon'
import { formatPhone } from '../formatters'
import { buildPayload, detectLocalTimezoneEnum } from './helpers'

function toIsoDate(date: Date): string {
  return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd')
}
function parseLocalDate(yyyyMmDd: string): Date {
  return DateTime.fromISO(yyyyMmDd).toJSDate()
}

export default function ScheduleEntryDialog(props: {
  companyId: string
  kind: 'TIME_OFF'
  roles: RoleWithMembers[]
  staff: Staff[]
  editing?: ScheduleEntry
  onClose: () => void
  onSaved: () => void
}) {
  const { companyId, kind, staff, editing, onClose, onSaved } = props
  const { isSuperAdmin, currentCompany } = useAppAuth()
  const isEdit = !!editing
  const [staffId, setStaffId] = useState(editing?.staffId ?? '')

  const [substituteStaffId, setSubstituteStaffId] = useState(
    editing?.substitutes?.[0]?.staffId ?? '',
  )

  // The viewer's calendar day — the UTC day is already tomorrow in US evenings.
  const today = DateTime.now().toISODate() ?? ''
  const [startDate, setStartDate] = useState(
    editing?.startDate
      ? new Date(editing.startDate).toISOString().slice(0, 10)
      : today,
  )
  const [endDate, setEndDate] = useState(
    editing?.endDate
      ? new Date(editing.endDate).toISOString().slice(0, 10)
      : today,
  )
  const [timezoneEnum, setTimezoneEnum] = useState<TimezoneEnumValue | ''>(
    () =>
      toTimezoneEnumValue(editing?.timezone ?? undefined) ??
      toTimezoneEnumValue(currentCompany?.timezoneName ?? undefined) ??
      detectLocalTimezoneEnum(),
  )

  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!staffId) return toast.error('Pick a staff member')

    const ianaTimezone = toIANATimezone(timezoneEnum || undefined) ?? null

    if (!startDate || !endDate) {
      return toast.error('Pick a start and end date')
    }

    if (!ianaTimezone) {
      return toast.error('Pick a timezone')
    }

    if (!substituteStaffId) {
      return toast.error('Pick a fallback staff')
    }
    const substitutes: SubstituteInput[] = [
      { position: 0, staffId: substituteStaffId, roleId: null },
    ]

    setSaving(true)
    try {
      const payload = buildPayload({
        startDate,
        endDate,
        ianaTimezone,
      })

      const target = {
        kind,
        preset: 'CUSTOM' as const,
        roleId: null,
        staffId,
      }

      const baseInput = { ...target, ...payload, substitutes }

      if (isEdit && editing) {
        if (isSuperAdmin) {
          await directoryAdminApiClient.updateScheduleEntry(
            companyId,
            editing.id,
            baseInput,
          )
        } else {
          await directoryApiClient.updateScheduleEntry(editing.id, baseInput)
        }
      } else {
        if (isSuperAdmin) {
          await directoryAdminApiClient.createScheduleEntry(
            companyId,
            baseInput,
          )
        } else {
          await directoryApiClient.createScheduleEntry(baseInput)
        }
      }

      toast.success(isEdit ? 'Time off updated' : 'Time off added')
      onSaved()
    } catch (e: any) {
      console.error(e)
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const dialogTitle = isEdit ? 'Edit time off' : 'Add time off'

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-1">
          {/* Who: staff picker */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-foreground">Who</Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <StaffSearchDropdown
                  staff={staff}
                  selectedId={staffId}
                  onSelect={setStaffId}
                />
              </div>
            </div>
          </div>

          {/* When: date range + timezone */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-foreground">When</Label>

            <DateRangePopover
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
            />
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">
                Timezone
              </Label>
              <div className="w-[15.5rem] max-w-full">
                <TimezoneSelector
                  value={timezoneEnum || undefined}
                  onValueChange={setTimezoneEnum}
                  placeholder="Timezone"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-foreground">
              Fallback
            </Label>
            <Text variant="muted" className="text-xs">
              Who picks up when this person is out?
            </Text>
            <StaffSearchDropdown
              staff={staff}
              selectedId={substituteStaffId}
              onSelect={setSubstituteStaffId}
            />
          </div>
        </div>
        <DialogFooter className="mt-6">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function StaffSearchDropdown(props: {
  staff: Staff[]
  selectedId: string
  onSelect: (id: string) => void
}) {
  const items: SearchDropdownItem<Staff>[] = props.staff.map(staffMember => ({
    id: staffMember.id,
    label: staffMember.name,
    sublabel:
      [formatPhone(staffMember.phone), staffMember.email]
        .filter(Boolean)
        .join(' · ') || undefined,
    data: staffMember,
  }))
  const selected = items.find(item => item.id === props.selectedId) ?? null
  return (
    <SearchDropdown<Staff>
      items={items}
      selectedItems={selected}
      onSelect={item => {
        const next = Array.isArray(item) ? item[0] : item
        props.onSelect(next?.id ?? '')
      }}
      placeholder="Pick a staff member"
      searchPlaceholder="Search by name, phone, or email"
      buttonClassName="w-full"
    />
  )
}

function DateRangePopover(props: {
  startDate: string
  endDate: string
  setStartDate: (value: string) => void
  setEndDate: (value: string) => void
}) {
  const { startDate, endDate, setStartDate, setEndDate } = props
  const date = useMemo<DateRange | undefined>(() => {
    if (!startDate && !endDate) return undefined
    return {
      from: startDate ? parseLocalDate(startDate) : undefined,
      to: endDate ? parseLocalDate(endDate) : undefined,
    }
  }, [startDate, endDate])
  return (
    <DateRangePicker
      className="w-full"
      date={date}
      onDateChange={range => {
        setStartDate(range?.from ? toIsoDate(range.from) : '')
        setEndDate(range?.to ? toIsoDate(range.to) : '')
      }}
    />
  )
}
